import {
    GroupsOutlined,
    BeachAccessOutlined,
    FreeBreakfastOutlined,
    WorkOutlineOutlined,
    OpacityOutlined,
} from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import { useStore } from "../../../store/store";

type IconToggleProps = {
    active: boolean;
    onToggle: () => void;
    tooltipOn: string;
    tooltipOff: string;
    icon: JSX.Element;
};

const IconToggle = ({ active, onToggle, tooltipOn, tooltipOff, icon }: IconToggleProps) => (
    <Tooltip title={active ? tooltipOn : tooltipOff}>
        <IconButton
            color="secondary"
            size="small"
            onClick={onToggle}
            sx={{
                border: "1px solid",
                borderColor: "secondary.main",
                backgroundColor: active ? "secondary.main" : "transparent",
                color: active ? "secondary.contrastText" : "secondary.main",
                "&:hover": { backgroundColor: active ? "secondary.dark" : "action.hover" },
            }}
        >
            {icon}
        </IconButton>
    </Tooltip>
);

export const SessionsCalendarOverlayToggle = () => {
    const {
        calendar: {
            scheduleOverlayEnabled,
            setScheduleOverlayEnabled,
            scheduleOverlayShowTimeOff,
            setScheduleOverlayShowTimeOff,
            scheduleOverlayDiscountBreaks,
            setScheduleOverlayDiscountBreaks,
            scheduleOverlayShowBackoffice,
            setScheduleOverlayShowBackoffice,
            scheduleOverlayGhostBookings,
            setScheduleOverlayGhostBookings,
            calendarView,
        },
    } = useStore();

    const incompatibleViews = ["month", "agenda", "year"];
    const isCompatibleView = !calendarView || !incompatibleViews.includes(calendarView);
    const isDisabled = !isCompatibleView;
    const tooltipMessage = isDisabled ? "Disponível apenas nas vistas Semana e Dia" : "";

    const handleToggleOverlay = () => {
        setScheduleOverlayEnabled(!scheduleOverlayEnabled);
    };

    const overlayButton = (
        <Button
            startIcon={<GroupsOutlined />}
            variant={scheduleOverlayEnabled ? "contained" : "outlined"}
            color="secondary"
            size="medium"
            onClick={handleToggleOverlay}
            disabled={isDisabled}
            sx={{ width: "100%" }}
        >
            Horários
        </Button>
    );

    const overlayButtonWithTooltip = isDisabled ? (
        <Tooltip title={tooltipMessage}>
            <span style={{ width: "100%", display: "inline-block" }}>{overlayButton}</span>
        </Tooltip>
    ) : (
        overlayButton
    );

    const showSecondaryToggles = scheduleOverlayEnabled && !isDisabled;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", ml: 2 }}>
            {overlayButtonWithTooltip}
            {showSecondaryToggles ? (
                <Stack direction="row" spacing={1} justifyContent="center">
                    <IconToggle
                        active={scheduleOverlayShowTimeOff}
                        onToggle={() => setScheduleOverlayShowTimeOff(!scheduleOverlayShowTimeOff)}
                        tooltipOn="Ocultar folgas"
                        tooltipOff="Mostrar folgas"
                        icon={<BeachAccessOutlined fontSize="small" />}
                    />
                    <IconToggle
                        active={scheduleOverlayDiscountBreaks}
                        onToggle={() => setScheduleOverlayDiscountBreaks(!scheduleOverlayDiscountBreaks)}
                        tooltipOn="Desconto de intervalo e limpeza ativo"
                        tooltipOff="Descontar tempo de intervalo e limpeza"
                        icon={<FreeBreakfastOutlined fontSize="small" />}
                    />
                    <IconToggle
                        active={scheduleOverlayShowBackoffice}
                        onToggle={() => setScheduleOverlayShowBackoffice(!scheduleOverlayShowBackoffice)}
                        tooltipOn="Ocultar horários de backoffice"
                        tooltipOff="Mostrar horários de backoffice"
                        icon={<WorkOutlineOutlined fontSize="small" />}
                    />
                    <IconToggle
                        active={scheduleOverlayGhostBookings}
                        onToggle={() => setScheduleOverlayGhostBookings(!scheduleOverlayGhostBookings)}
                        tooltipOn="Mostrar marcações normais"
                        tooltipOff="Atenuar marcações para ver os horários"
                        icon={<OpacityOutlined fontSize="small" />}
                    />
                </Stack>
            ) : null}
        </Box>
    );
};
